/**
 * Microsoft Clarity custom tags — pure, allow-listed, non-PII.
 *
 * Only these keys can ever be sent. Values are either a fixed vocabulary or a
 * positive integer id; anything else (titles, names, search text, phones,
 * addresses, coordinates) is dropped here before it can reach Clarity.
 */

export const CLARITY_PAGE_TYPES = [
  'home',
  'listing',
  'search',
  'seo_landing',
  'property_detail',
  'post_property',
  'account',
  'premium',
  'payment',
  'content',
  'other',
] as const

export type ClarityPageType = (typeof CLARITY_PAGE_TYPES)[number]
export type ClarityListingIntent = 'sale' | 'rent' | 'commercial'

export interface ClarityTagInput {
  page_type?: string | null
  city_id?: number | string | null
  area_id?: number | string | null
  category_id?: number | string | null
  property_id?: number | string | null
  listing_intent?: string | null
}

export type ClarityTags = Partial<Record<keyof ClarityTagInput, string>>

const ID_KEYS = ['city_id', 'area_id', 'category_id', 'property_id'] as const
const ID_RE = /^[1-9][0-9]{0,17}$/

function safeId(value: number | string | null | undefined): string | undefined {
  if (value == null) return undefined
  const text = typeof value === 'number'
    ? (Number.isSafeInteger(value) ? String(value) : '')
    : value.trim()
  return ID_RE.test(text) ? text : undefined
}

/** Maps app intents (`buy`/`sale`/`rent`/`commercial`) to the tag vocabulary. */
export function clarityListingIntent(value: string | null | undefined): ClarityListingIntent | undefined {
  const v = (value ?? '').trim().toLowerCase()
  if (v === 'buy' || v === 'sale') return 'sale'
  if (v === 'rent') return 'rent'
  if (v === 'commercial') return 'commercial'
  return undefined
}

export function sanitizeClarityTags(input: ClarityTagInput): ClarityTags {
  const tags: ClarityTags = {}
  const pageType = (input.page_type ?? '').trim()
  if ((CLARITY_PAGE_TYPES as readonly string[]).includes(pageType)) tags.page_type = pageType
  for (const key of ID_KEYS) {
    const id = safeId(input[key])
    if (id) tags[key] = id
  }
  const intent = clarityListingIntent(input.listing_intent)
  if (intent) tags.listing_intent = intent
  return tags
}

const ACCOUNT_RE = /^\/(dashboard|editprofile|saved-properties|my-property|my-inquiries|my-site-visits)(\/|$)/
const PREMIUM_RE = /^\/(ourproducts|premium_benefit|premium_benifits|premiumcourselist|learnoverview|video|calculator|realestate-book)(\/|$)/
const PAYMENT_RE = /^\/(premium-package\/payment-|paymentdetails|purchase_history)/
const CONTENT_RE = /^\/(blog|magazine|media|about-us|contact-us|faq|achievements|feedback|privacy-policy|terms-and-conditions|home-loan-emi|home-loan-eligibility)(\/|$)/
const POST_RE = /^\/(add-property|edit-property)(\/|$)/
const SEO_LANDING_RE = /^\/(?:[a-z0-9-]+-for-(?:sale|rent)-in-[a-z0-9-]+|propert(?:y|ies)-in-[a-z0-9-]+)$/

/**
 * Tags derivable from the URL alone. Only the listing intent and the numeric
 * property id prefix are read — never slugs, search text or other query values.
 */
export function clarityRouteTags(pathname: string, search = ''): ClarityTags {
  const path = (pathname || '/').toLowerCase().replace(/\/+$/, '') || '/'
  const query = new URLSearchParams(search)

  let pageType: ClarityPageType = 'other'
  let propertyId: string | undefined
  let intent: string | null = null

  if (path === '/') pageType = 'home'
  else if (path === '/properties') {
    pageType = (query.get('search') ?? '').trim() ? 'search' : 'listing'
    intent = query.get('intent')
  } else if (path.startsWith('/property/')) {
    pageType = 'property_detail'
    propertyId = /^\/property\/([0-9]+)/.exec(path)?.[1]
  } else if (POST_RE.test(path)) pageType = 'post_property'
  else if (ACCOUNT_RE.test(path)) pageType = 'account'
  else if (PAYMENT_RE.test(path)) pageType = 'payment'
  else if (PREMIUM_RE.test(path)) pageType = 'premium'
  else if (CONTENT_RE.test(path)) pageType = 'content'
  else if (SEO_LANDING_RE.test(path)) {
    pageType = 'seo_landing'
    intent = /-for-sale-in-/.test(path) ? 'sale' : /-for-rent-in-/.test(path) ? 'rent' : query.get('intent')
  }

  return sanitizeClarityTags({ page_type: pageType, property_id: propertyId, listing_intent: intent })
}
